import React, { FunctionComponent, useState, MouseEvent, ReactElement } from 'react';
import {
  PromoWithTagsGrid,
  PromoWithTagsExtraContentWrapper,
  PromoWithTagsContentWrapper,
  PromoWithTagsSubtitle,
  PromoWithTagsText,
  PromoWithTagsContainer,
  ButtonsContainer,
  PromoWithTagsTagWrapper,
  PromoWithTagsAdditionalText,
  TextLinksContainer,
  TextLinkWrapper,
  ExtraContentWrapper,
  IconWrapper,
  PrimaryButtonWrapper,
  MobileTitleWrapper,
  PromoWithTagsTypeTags,
  PromoWithTagsHeader,
  PromoWithTagsTextLink,
  TimerWrapper,
  EndDateText,
} from './PromoWithTags.style';
import { AltHeader, Icon, PrimaryButton, SecondaryButton, TertiaryButton } from '../../atoms';
import ImageAspectRatioWrapper from '../../atoms/ImageAspectRatioWrapper';
import { AspectRatio } from '../../../types/image';
import { IPromoWithTagsProps } from '../../../types/promoWithTags';
import { EditorialLink } from '../../../types/editorial';
import { ButtonType, Colors } from '../../../types';
import { devices } from '../../../styles/viewports';
import { TypographyLevel } from '../../../types/typography';
import { truncate } from '../../../helpers/htmlStrings';
import Timer from '../../atoms/Timer';
import { IButtonProps } from '../../../types/buttonTypes';

const LENGTH_TEXT = 28;
const LENGTH_TEXT_PARAGRAPH = 130;

const buttonTypeToButton = {
  [ButtonType.Primary]: PrimaryButton,
  [ButtonType.Secondary]: SecondaryButton,
  [ButtonType.Tertiary]: TertiaryButton,
};

const PromoWithTags: FunctionComponent<IPromoWithTagsProps> = ({
  imagePosition = 'left',
  title = '',
  titleSize = 'large',
  subtitle,
  text,
  textLinks,
  links,
  children,
  aboveTitleTags,
  belowTitleTags,
  middleText,
  bottomText,
  image,
  asCard = false,
  disableCTA = false,
  disabledCTACopy = 'Sold Out',
  timerParams,
}) => {
  const [showExtraContent, setShowExtraContent] = useState(false);
  const [isTimerActive, setIsTimerActive] = useState(true);

  const getTitleLevel = (titleSize: string, isCard: boolean): TypographyLevel => {
    if (isCard) {
      return 5;
    }
    if (titleSize === 'large') {
      return 2;
    }
    return 3;
  };

  const imageToLeft = imagePosition === 'left';
  const titleLevel = getTitleLevel(titleSize, asCard);
  const isExtraContentPresent = !!children;
  const anchorLink = `${title.replace(' ', '-').toLocaleLowerCase()}-content`;

  const handleExtraContent = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowExtraContent(true);
    if (window.matchMedia && window.matchMedia(devices.mobile).matches) {
      window.location.href = `#${anchorLink}`;
    }
  };
  const defaultOnClickHandler = isExtraContentPresent ? handleExtraContent : undefined;

  const firstButton = links?.[0];
  const { text: firstButtonText = '', ...restFirstButton } = firstButton || {};
  const primaryButtonTextTruncate = truncate(firstButtonText, LENGTH_TEXT);

  const secondButton = links?.[1];
  const { text: secondButtonText = '', ...restSecondButton } = secondButton || {};
  const secondButtonTextTruncate = truncate(secondButtonText, LENGTH_TEXT);
  const textTruncate = asCard ? truncate(text || '', LENGTH_TEXT_PARAGRAPH) : text;

  const FirstButtonComponent = firstButton?.buttonType
    ? (buttonTypeToButton[firstButton.buttonType] as FunctionComponent<IButtonProps>)
    : PrimaryButton;

  const SecondButtonComponent = secondButton?.buttonType
    ? (buttonTypeToButton[secondButton.buttonType] as typeof TertiaryButton)
    : TertiaryButton;

  const textLinkItems = textLinks
    ? textLinks.map((link: EditorialLink, index) => {
        const { text: textLinkText = '', ...restTextLink } = link;
        return (
          <TextLinkWrapper key={index}>
            <PromoWithTagsTextLink {...restTextLink}>{textLinkText}</PromoWithTagsTextLink>
          </TextLinkWrapper>
        );
      })
    : null;

  const renderTimerBlock = (): ReactElement | null => {
    if (!timerParams) return null;

    if (!isTimerActive && timerParams.endDateText) {
      return (
        <EndDateText data-testid="promo-with-tags-timer-end-date-text">
          <AltHeader level={5}>{timerParams.endDateText}</AltHeader>
        </EndDateText>
      );
    }

    return (
      <TimerWrapper data-testid="promo-with-tags-timer-wrapper">
        <Timer endDateHandler={() => setIsTimerActive(false)} endDate={timerParams.endDate} title={timerParams.title} />
      </TimerWrapper>
    );
  };

  return (
    <PromoWithTagsGrid id={anchorLink} data-testid="grid-wrapper" imageToLeft={imageToLeft} asCard={asCard}>
      <PromoWithTagsContentWrapper
        data-testid="content-wrapper"
        imageToLeft={imageToLeft}
        hideSection={showExtraContent}
        asCard={asCard}
      >
        {renderTimerBlock()}
        <PromoWithTagsContainer>
          {!!aboveTitleTags?.length && (
            <PromoWithTagsTagWrapper marginBottom={16}>
              <PromoWithTagsTypeTags list={aboveTitleTags} />
            </PromoWithTagsTagWrapper>
          )}
          <PromoWithTagsHeader semanticLevel={2} level={titleLevel}>
            {title}
          </PromoWithTagsHeader>
        </PromoWithTagsContainer>
        {subtitle && <PromoWithTagsSubtitle>{subtitle}</PromoWithTagsSubtitle>}
        {!!belowTitleTags?.length && (
          <PromoWithTagsTagWrapper marginBottom={8}>
            <PromoWithTagsTypeTags list={belowTitleTags} />
          </PromoWithTagsTagWrapper>
        )}
        <PromoWithTagsText dangerouslySetInnerHTML={{ __html: textTruncate as string }} />
        {middleText && <PromoWithTagsAdditionalText dangerouslySetInnerHTML={{ __html: middleText as string }} />}
        {bottomText && <PromoWithTagsAdditionalText dangerouslySetInnerHTML={{ __html: bottomText as string }} />}
        {!!textLinks?.length && <TextLinksContainer>{textLinkItems}</TextLinksContainer>}
        {links && (
          <ButtonsContainer
            hasTextLinks={!!textLinks?.length}
            data-testid="buttons-wrapper"
            primaryButtonTextLength={firstButtonText.length}
            tertiaryButtonTextLength={secondButtonText.length}
          >
            {firstButton && (
              <PrimaryButtonWrapper>
                {disableCTA ? (
                  <FirstButtonComponent bgColor={Colors.MidGrey} onClick={() => null} data-testid="diabled-link">
                    {disabledCTACopy || primaryButtonTextTruncate}
                  </FirstButtonComponent>
                ) : (
                  !showExtraContent && (
                    <FirstButtonComponent onClick={defaultOnClickHandler} {...restFirstButton}>
                      {primaryButtonTextTruncate}
                    </FirstButtonComponent>
                  )
                )}
              </PrimaryButtonWrapper>
            )}
            {secondButton && (
              <SecondButtonComponent {...restSecondButton}>{secondButtonTextTruncate}</SecondButtonComponent>
            )}
          </ButtonsContainer>
        )}
      </PromoWithTagsContentWrapper>
      <PromoWithTagsExtraContentWrapper data-testid="extra-content-wrapper" imageToLeft={imageToLeft}>
        {showExtraContent && (
          <MobileTitleWrapper>
            <PromoWithTagsHeader semanticLevel={2} level={titleLevel}>
              {title}
            </PromoWithTagsHeader>
          </MobileTitleWrapper>
        )}
        <ImageAspectRatioWrapper aspectRatio={AspectRatio['4:3']}>
          {isExtraContentPresent && showExtraContent ? (
            <ExtraContentWrapper>
              {children}
              <IconWrapper
                data-testid="extra-content-close-button"
                onClick={() => setShowExtraContent(false)}
                tabIndex={0}
                onKeyPress={(e) => {
                  e.key === 'Enter' && setShowExtraContent(false);
                }}
              >
                <Icon iconName="Close" color={Colors.Black} />
              </IconWrapper>
            </ExtraContentWrapper>
          ) : (
            image && <img data-testid="promo-with-tags-image" src={image.src} alt={image.alt} />
          )}
        </ImageAspectRatioWrapper>
      </PromoWithTagsExtraContentWrapper>
    </PromoWithTagsGrid>
  );
};

export default PromoWithTags;
